/**
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business PJ Sandbox 白板のドラッグ&ドロップエンジン。ブロックの移動・リサイズ、候補案件の白板への直接配置、未配置ブロックの週セクションへのドロップ（候補案件化）、候補案件の週セクション間移動を担う。ポインタ座標をキャンバスのセル（メンバー行 / 日）へ変換し、重なり上限を超える配置を弾き、確定した操作を編集履歴用の操作として記録する。描画は SandboxBoard / CandidatePool、保存は usePjSandboxPersistence に委ね、ここは「掴んでから離すまで」だけを持つ
 * @graph-connects placementToken [calls] 編集履歴に載せる配置トークンの組み立て
 * @graph-connects blockToCandidate [calls] 未配置ブロックの候補案件への変換
 * @graph-connects maxConcurrency [calls] 同一行の重なり数の算出
 */
import {
  useCallback,
  useRef,
  useState,
  type PointerEvent as ReactPointerEvent,
  type RefObject,
  type Dispatch,
  type SetStateAction,
} from 'react';
import type { PjSandboxOperationInput } from '@cortex/domain-product-innovation-lab-types';
import { blockToCandidate } from './blockToCandidate.js';
import { DAY_WIDTH, HEADER_HEIGHT, clamp, defaultSpanFromPt } from './constants.js';
import type { PoolWeek } from './constants.js';
import { MAX_OVERLAP, maxConcurrency } from './layout.js';
import { placementToken } from './persistence.js';
import type {
  CandidateCase,
  DragPreview,
  SandboxBlock,
  SandboxDay,
  SandboxMember,
} from './types.js';

/** ドラッグ操作の内部状態（ref 保持）。移動 / リサイズ / 候補からの新規作成の 3 種。 */
type DragMeta =
  | {
      type: 'move';
      blockId: string;
      span: number;
      grabDayOffset: number;
      /**
       * ドラッグ開始時の開始日インデックス。表示期間より前に始まった（左端で切り取られた）
       * 配置は負値を取る。移動時の下限クランプに使い、行だけ変える移動で真の開始日が
       * 期間の起点へ書き換わるのを防ぐ。未配置（トレイ）からのドラッグは 0。
       */
      fromStartDay: number;
      fromTray: boolean;
      /** ドラッグ開始時点の配置トークン（編集履歴の before に載せる）。未配置なら null。 */
      fromToken: string | null;
      /** ドラッグ対象の表示名（編集履歴の target に載せる）。 */
      blockName: string;
    }
  | {
      type: 'resize';
      blockId: string;
      startDay: number;
      rowIndex: number;
      /** リサイズ前の跨ぎ日数（編集履歴の before に載せる）。 */
      fromSpan: number;
      blockName: string;
    }
  | {
      type: 'create';
      /** ドラッグ元の候補案件 ID（週セクション間移動で参照する）。 */
      candidateId: string;
      template: {
        name: string;
        sizePt: number;
        kind: SandboxBlock['kind'];
        issueKey: string | undefined;
      };
      teamKey: string;
      span: number;
      /** ドラッグ開始時点の週インデックス（週移動の before に載せる）。 */
      fromWeekIndex: number;
    };

/** clientX/Y 直下の候補プール週セクションのインデックスを返す（無ければ null）。 @graph-connects none */
function poolWeekAtPoint(clientX: number, clientY: number): number | null {
  const el = document.elementFromPoint(clientX, clientY);
  const section = el?.closest('[data-pj-week]');
  if (section === null || section === undefined) return null;
  const raw = section.getAttribute('data-pj-week');
  if (raw === null) return null;
  const idx = Number(raw);
  return Number.isInteger(idx) ? idx : null;
}

interface UseSandboxDndInput {
  /** 日エリア（スクロールする canvas）の ref。座標計算の原点になる。 */
  canvasRef: RefObject<HTMLDivElement | null>;
  /** 白板の両軸スクロールコンテナの ref。ドラッグの「白板内 / 外」判定に使う。 */
  boardViewportRef: RefObject<HTMLDivElement | null>;
  blocks: SandboxBlock[];
  setBlocks: Dispatch<SetStateAction<SandboxBlock[]>>;
  setCandidates: Dispatch<SetStateAction<CandidateCase[]>>;
  members: SandboxMember[];
  days: SandboxDay[];
  weeks: PoolWeek[];
  rowTops: number[];
  rowHeights: number[];
  /** 表示中の白板（チーム）の key。新規ブロックに付与する。 */
  teamKey: string;
  /** 編集権限。false のときはドラッグ自体を開始しない。 */
  canEdit: boolean;
  /** 確定した操作を編集履歴へ積む。 */
  recordOp: (op: PjSandboxOperationInput) => void;
  /** 新しいブロック ID を払い出す。 */
  nextBlockId: () => string;
  /** 新しい候補案件 ID を払い出す。 */
  nextCandidateId: () => string;
}

interface UseSandboxDndResult {
  /** ドラッグ中の配置プレビュー（グリッドに点線枠で描画）。 */
  preview: DragPreview | null;
  /** ハイライトする候補プールの週セクション（ドロップ先予告）。 */
  hoverWeek: number | null;
  onBlockPointerDown: (block: SandboxBlock, e: ReactPointerEvent) => void;
  onResizePointerDown: (block: SandboxBlock, e: ReactPointerEvent) => void;
  onCandidatePointerDown: (candidate: CandidateCase, e: ReactPointerEvent) => void;
}

/**
 * 白板の DnD 一式を引き受けるフック。
 * @graph-connects none
 */
export function useSandboxDnd(input: UseSandboxDndInput): UseSandboxDndResult {
  const {
    canvasRef,
    boardViewportRef,
    setBlocks,
    setCandidates,
    teamKey,
    canEdit,
    recordOp,
    nextBlockId,
    nextCandidateId,
  } = input;

  const [preview, setPreview] = useState<DragPreview | null>(null);
  const [hoverWeek, setHoverWeek] = useState<number | null>(null);

  const dragRef = useRef<DragMeta | null>(null);
  const previewRef = useRef<DragPreview | null>(null);

  // ポインタ移動/終了ハンドラから常に最新値を読むための ref（ハンドラ自体は安定参照にする）。
  const stateRef = useRef({
    blocks: input.blocks,
    members: input.members,
    days: input.days,
    weeks: input.weeks,
    rowTops: input.rowTops,
    rowHeights: input.rowHeights,
  });
  stateRef.current = {
    blocks: input.blocks,
    members: input.members,
    days: input.days,
    weeks: input.weeks,
    rowTops: input.rowTops,
    rowHeights: input.rowHeights,
  };

  /** 対象行に配置を加えたとき、重なりが MAX_OVERLAP を超えないか判定する。 */
  const isPlacementValid = useCallback(
    (rowIndex: number, startDay: number, spanDays: number, excludeId: string | undefined) => {
      const intervals = [{ startDay, spanDays }];
      for (const b of stateRef.current.blocks) {
        const p = b.placement;
        if (p === null || p.rowIndex !== rowIndex || b.id === excludeId) continue;
        intervals.push({ startDay: p.startDay, spanDays: p.spanDays });
      }
      return maxConcurrency(intervals) <= MAX_OVERLAP;
    },
    [],
  );

  /** ポインタ座標をキャンバス内のセル（日 / 行）に変換する。 */
  const cellFromPointer = useCallback(
    (clientX: number, clientY: number) => {
      const canvas = canvasRef.current;
      if (canvas === null) return null;
      const rect = canvas.getBoundingClientRect();
      const x = clientX - rect.left;
      const y = clientY - rect.top;
      const { days: ds, rowTops: tops, rowHeights: heights } = stateRef.current;
      const day = Math.floor(x / DAY_WIDTH);
      const trackBottom =
        tops.length > 0 ? tops[tops.length - 1] + heights[heights.length - 1] : HEADER_HEIGHT;
      // 可変行高に対応した行ヒットテスト（上端 Y の累積から探す）。
      let row = -1;
      for (let i = 0; i < tops.length; i++) {
        if (y >= tops[i] && y < tops[i] + heights[i]) {
          row = i;
          break;
        }
      }
      // canvas の bounding rect は縦横スクロールで clip 前の全幅・全高を返す。実際に見えている
      // スクロール viewport（両軸スクロールコンテナ）内かも判定して誤検知を防ぐ。
      const viewport = boardViewportRef.current;
      const withinViewport =
        viewport !== null &&
        (() => {
          const v = viewport.getBoundingClientRect();
          return clientX >= v.left && clientX <= v.right && clientY >= v.top && clientY <= v.bottom;
        })();
      const inside =
        withinViewport &&
        x >= 0 &&
        x <= ds.length * DAY_WIDTH &&
        y >= HEADER_HEIGHT &&
        y <= trackBottom;
      return { day, row, x, inside };
    },
    [canvasRef, boardViewportRef],
  );

  /** 白板の中で確定したドラッグを反映し、変化があれば編集履歴へ積む。 */
  const commitOnBoard = useCallback(
    (drag: DragMeta, pv: DragPreview) => {
      const { members: ms, days: ds } = stateRef.current;
      const toToken = placementToken(pv, ms, ds);

      if (drag.type === 'resize') {
        setBlocks((prev) =>
          prev.map((b) =>
            b.id === drag.blockId && b.placement !== null
              ? { ...b, placement: { ...b.placement, spanDays: pv.spanDays } }
              : b,
          ),
        );
        if (drag.fromSpan === pv.spanDays) return;
        recordOp({
          action: 'block_resize',
          targetName: drag.blockName,
          before: drag.fromSpan.toString(),
          after: pv.spanDays.toString(),
          count: null,
        });
        return;
      }

      if (drag.type === 'move') {
        setBlocks((prev) =>
          prev.map((b) =>
            b.id === drag.blockId
              ? {
                  ...b,
                  placement: {
                    rowIndex: pv.rowIndex,
                    startDay: pv.startDay,
                    spanDays: pv.spanDays,
                  },
                }
              : b,
          ),
        );
        if (drag.fromToken === toToken) return;
        recordOp({
          action: drag.fromToken === null ? 'block_place' : 'block_move',
          targetName: drag.blockName,
          before: drag.fromToken,
          after: toToken,
          count: null,
        });
        return;
      }

      const newBlock: SandboxBlock = {
        id: nextBlockId(),
        name: drag.template.name,
        sizePt: drag.template.sizePt,
        kind: drag.template.kind,
        teamKey: drag.teamKey,
        placement: { rowIndex: pv.rowIndex, startDay: pv.startDay, spanDays: pv.spanDays },
        sourceCandidateId: drag.candidateId,
        ...(drag.template.issueKey === undefined ? {} : { issueKey: drag.template.issueKey }),
      };
      setBlocks((prev) => [...prev, newBlock]);
      recordOp({
        action: 'block_place',
        targetName: drag.template.name,
        before: null,
        after: toToken,
        count: null,
      });
    },
    [setBlocks, recordOp, nextBlockId],
  );

  /** 白板の外（候補プールの週セクション上）で離したドラッグを反映する。 */
  const commitOnPool = useCallback(
    (drag: DragMeta, clientX: number, clientY: number) => {
      const { weeks: wks, blocks: bs } = stateRef.current;

      if (drag.type === 'create') {
        // 白板に置けなかった候補ドラッグが週セクション上なら、その週へ移動する。
        const wk = poolWeekAtPoint(clientX, clientY);
        if (wk === null || wk === drag.fromWeekIndex) return;
        setCandidates((prev) =>
          prev.map((c) => (c.id === drag.candidateId ? { ...c, weekIndex: wk } : c)),
        );
        recordOp({
          action: 'candidate_move_week',
          targetName: drag.template.name,
          before: wks[drag.fromWeekIndex]?.label ?? null,
          after: wks[wk]?.label ?? null,
          count: null,
        });
        return;
      }

      // 未配置ブロックを週セクションへドロップ → その週の候補案件に変換（トレイからは除去）。
      if (drag.type !== 'move' || !drag.fromTray) return;
      const wk = poolWeekAtPoint(clientX, clientY);
      const block = bs.find((b) => b.id === drag.blockId);
      if (wk === null || block === undefined) return;
      setCandidates((prev) => [...prev, blockToCandidate(block, wk, nextCandidateId(), teamKey)]);
      setBlocks((prev) => prev.filter((b) => b.id !== drag.blockId));
      recordOp({
        action: 'block_to_candidate',
        targetName: block.name,
        before: null,
        after: wks[wk]?.label ?? null,
        count: null,
      });
    },
    [setBlocks, setCandidates, recordOp, nextCandidateId, teamKey],
  );

  const handlePointerMove = useCallback(
    (e: PointerEvent) => {
      const drag = dragRef.current;
      if (drag === null) return;
      const cell = cellFromPointer(e.clientX, e.clientY);
      if (cell === null) return;
      const { days: ds, members: ms } = stateRef.current;

      const excludeId = drag.type === 'create' ? undefined : drag.blockId;
      let next: DragPreview;
      if (drag.type === 'resize') {
        const rawSpan = Math.round(cell.x / DAY_WIDTH) - drag.startDay;
        const span = clamp(rawSpan, 1, ds.length - drag.startDay);
        next = {
          rowIndex: drag.rowIndex,
          startDay: drag.startDay,
          spanDays: span,
          inside: true,
          valid: isPlacementValid(drag.rowIndex, drag.startDay, span, excludeId),
        };
      } else {
        const span = drag.span;
        const grab = drag.type === 'move' ? drag.grabDayOffset : 0;
        // 下限は原則 0（期間内へ収める）だが、元々期間より前に始まっていた配置（負の開始日）は
        // その開始日まで下限を緩める。こうすると行だけ変える移動で真の開始日が起点へ丸められず、
        // 「開始日が毎週書き換わらない」不変量を移動操作でも保てる。
        const lowerBound = drag.type === 'move' ? Math.min(0, drag.fromStartDay) : 0;
        const startDay = clamp(cell.day - grab, lowerBound, Math.max(0, ds.length - span));
        const rowIndex = clamp(cell.row, 0, Math.max(0, ms.length - 1));
        next = {
          rowIndex,
          startDay,
          spanDays: span,
          inside: cell.inside,
          valid: cell.inside && isPlacementValid(rowIndex, startDay, span, excludeId),
        };
      }
      previewRef.current = next;
      setPreview(next);
      // 候補ドラッグ or 未配置ブロックのドラッグが白板の外にあるとき、直下の週セクションを
      // ハイライト（週への配置予告）。
      const canDropToWeek = drag.type === 'create' || (drag.type === 'move' && drag.fromTray);
      setHoverWeek(canDropToWeek && !next.inside ? poolWeekAtPoint(e.clientX, e.clientY) : null);
    },
    [cellFromPointer, isPlacementValid],
  );

  const handlePointerUp = useCallback(
    (e: PointerEvent) => {
      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('pointerup', handlePointerUp);

      const drag = dragRef.current;
      const pv = previewRef.current;
      dragRef.current = null;
      previewRef.current = null;
      setPreview(null);
      setHoverWeek(null);

      if (drag === null) return;
      // 配置可能（グリッド内 + 重なり数 OK）なときだけ白板側で確定する。
      if (pv !== null && pv.inside && pv.valid) {
        commitOnBoard(drag, pv);
        return;
      }
      commitOnPool(drag, e.clientX, e.clientY);
    },
    [handlePointerMove, commitOnBoard, commitOnPool],
  );

  const startListening = useCallback(() => {
    window.addEventListener('pointermove', handlePointerMove);
    window.addEventListener('pointerup', handlePointerUp);
  }, [handlePointerMove, handlePointerUp]);

  const onBlockPointerDown = useCallback(
    (block: SandboxBlock, e: ReactPointerEvent) => {
      if (!canEdit) return;
      e.preventDefault();
      const { members: ms, days: ds } = stateRef.current;
      const span = block.placement?.spanDays ?? defaultSpanFromPt(block.sizePt);
      let grab = 0;
      if (block.placement !== null) {
        const cell = cellFromPointer(e.clientX, e.clientY);
        if (cell !== null) grab = clamp(cell.day - block.placement.startDay, 0, span - 1);
      }
      dragRef.current = {
        type: 'move',
        blockId: block.id,
        span,
        grabDayOffset: grab,
        fromStartDay: block.placement?.startDay ?? 0,
        fromTray: block.placement === null,
        fromToken: placementToken(block.placement, ms, ds),
        blockName: block.name,
      };
      startListening();
    },
    [canEdit, cellFromPointer, startListening],
  );

  const onResizePointerDown = useCallback(
    (block: SandboxBlock, e: ReactPointerEvent) => {
      if (!canEdit) return;
      e.preventDefault();
      e.stopPropagation();
      if (block.placement === null) return;
      dragRef.current = {
        type: 'resize',
        blockId: block.id,
        startDay: block.placement.startDay,
        rowIndex: block.placement.rowIndex,
        fromSpan: block.placement.spanDays,
        blockName: block.name,
      };
      startListening();
    },
    [canEdit, startListening],
  );

  const onCandidatePointerDown = useCallback(
    (candidate: CandidateCase, e: ReactPointerEvent) => {
      if (!canEdit) return;
      e.preventDefault();
      dragRef.current = {
        type: 'create',
        candidateId: candidate.id,
        template: {
          name: candidate.title,
          sizePt: candidate.sizePt,
          kind: candidate.kind,
          issueKey: candidate.issueKey,
        },
        teamKey,
        span: defaultSpanFromPt(candidate.sizePt),
        fromWeekIndex: candidate.weekIndex,
      };
      startListening();
    },
    [canEdit, teamKey, startListening],
  );

  return { preview, hoverWeek, onBlockPointerDown, onResizePointerDown, onCandidatePointerDown };
}
